import {
  ReactElement,
  useEffect,
  useMemo,
  useState,
  useContext,
  lazy,
  Suspense
} from "react";
import "./CheckOut.css";
import { ReactComponent as Tick } from "../../assets/icons/round_check.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { stateType } from "../../store";
import { connect } from "react-redux";
import {
  setCheckOutError,
  setDirectPayment,
  setOrderDetails,
  setOrderInfo,
  setPageCount,
  setPaymentSuccess
} from "../../state_management/actions";
import CheckOutHeader from "../../elements/CheckOutHeader/CheckOutHeader";
import { cartItem, extrasCartItem } from "../../state_management/reducers/cart";
import { useNavigate } from "react-router-dom";
import {
  initialState as defaultOrderDetails,
  isComplete,
  orderInfo,
  orderType,
  proceed_val_type
} from "../../state_management/reducers/order";
import { asyncResType, useAsync } from "../../utilities/asyncFunc";
import useQuery from "../../utilities/Query";
import { directResolve } from "./getter";
import { baseName } from "../../settings";
import { settings } from "../../state_management/actionTypes";
import { setTimeError } from "../../state_management/actions/common";
import { useTimeout } from "../../utilities/useTimeout";
import { serviceItems } from "../../state_management/epics/settings";
import { currency } from "../../utilities/misc";
import CheckOutProvider, { ServiceDurationContext } from "./CheckOutProvider";
import { getCartTotal, only_gift } from "../../utilities/cartUtilities";
import {
  CartTotalContext,
  CouponObjectContext,
  LocaleContext
} from "../../AppProvider";

const CheckOutExtra = lazy(
  () => import("../../elements/CheckOutExtra/CheckOutExtra")
);
const CheckOutTime = lazy(
  () => import("../../elements/CheckOutTime/CheckOutTime")
);
const CheckOutAddress = lazy(
  () => import("../../elements/CheckOutAddress/CheckOutAddress")
);
const Payment = lazy(() => import("../../elements/CheckOutPayment"));
const PaymentDirect = lazy(
  () => import("../../elements/CheckOutPayment/PaymentDirect")
);

const CheckOut = ({
  pageCount,
  setPageCount,
  cart,
  extras,
  orderDetails,
  setOrderDetails,
  orderDirect,
  setDirectPayment,
  orderInfo,
  setOrderInfo,
  orderSuccess,
  setPaymentSuccess,
  setCheckOutError,
  settings,
  setTimeError,
  timeError,
  settingRes
}: {
  pageCount: proceed_val_type;
  setPageCount: (data: number) => void;
  cart: cartItem[];
  extras: extrasCartItem[];
  orderDetails: orderType;
  setOrderDetails: (data: orderType) => void;
  orderDirect: boolean;
  setDirectPayment: (data: boolean) => void;
  orderInfo: orderInfo;
  setOrderInfo: (data: orderInfo) => void;
  orderSuccess: boolean;
  setPaymentSuccess: (data: boolean) => void;
  setCheckOutError: (data: boolean) => void;
  settings: Partial<settings>;
  setTimeError: (data: boolean) => void;
  timeError: boolean;
  settingRes: asyncResType<serviceItems, settings>;
}) => {
  const query = useQuery();

  const direct = query.get("direct");
  const token = query.get("token");
  const id = query.get("id");

  const directUrl = !!(direct && token && id);

  const deactivateBars = orderSuccess || orderDirect || directUrl;
  const { t } = useContext(LocaleContext);
  const [cartTotal, setCartTotal] = useContext(CartTotalContext);
  const { CouponObject } = useContext(CouponObjectContext);

  const [__, setServiceDuration] = useContext(ServiceDurationContext);

  const duration = (() => {
    const getDuration = (duration: number) => {
      const isNum = !isNaN(parseFloat(`${duration}`));

      return isNum ? duration : 0;
    };
    const durationArray = cart.map(
      (item) => getDuration(item.item.duration) * item.quantity
    );

    const extraDurationArray = extras.map(
      (item) => getDuration(item.item.duration) * item.quantity
    );

    const durationTotal = [...durationArray, ...extraDurationArray].reduce(
      (a, b) => a + b,
      0
    );
    return durationTotal;
  })();

  useEffect(() => {
    setServiceDuration(duration);
  }, [duration]);

  const navigate = useNavigate();
  const cartCombined = [...cart, ...extras] as (cartItem | extrasCartItem)[];

  useEffect(() => {
    if (!cart.length && !deactivateBars) {
      navigate(baseName || "/", { replace: true });
    }
  }, [cart.length, deactivateBars, navigate]);

  useEffect(() => {
    // Remove the order info if date or cleaner details changes
    const changed =
      orderInfo?.time_start !== orderDetails.time_start ||
      orderInfo?.cleaner_id !== orderDetails.cleaner_id ||
      orderInfo?.random_cleaner !== orderDetails.random_cleaner;

    if (changed) {
      setOrderInfo(null);
    }
  }, [
    orderDetails.time_start,
    orderDetails.cleaner_id,
    orderDetails.random_cleaner,
    JSON.stringify(cartCombined)
  ]);

  const directRes = useAsync(
    {
      asyncFunc: directResolve,
      funcParams: { direct, orderId: id, token },
      immediate: true,
      clearOnError: true
    },
    [direct, id, token]
  );

  useEffect(() => {
    const detailRes = directRes.data;
    setDirectPayment(!!detailRes);
  }, [directRes.data]);

  useEffect(() => {
    return () => {
      setDefault();
    };
  }, []);

  useTimeout(
    {
      func: () => setTimeError(false),
      time: 5000,
      call: timeError
    },
    []
  );

  useEffect(() => {
    if (timeError) {
      setTimeError(false); // Remove error display on every date change that succeeds a timeError
    }
  }, [orderDetails.time_start]);

  useEffect(() => {
    setCheckOutError(false);
  }, [pageCount]);

  useEffect(() => {
    if (orderSuccess) {
      CouponObject.reset();
    }
  }, [orderSuccess]);

  const setDefault = () => {
    setPaymentSuccess(false);
    setDirectPayment(false);
    setCheckOutError(false);
    setOrderDetails(
      orderSuccess
        ? { ...defaultOrderDetails.details }
        : {
            ...orderDetails,
            time_start: null,
            cleaner_id: null,
            random_cleaner: true
          }
    );
    setOrderInfo(null);
    setTimeError(false);
    setPageCount(0);
  };

  /* Total after discount */
  const total = useMemo(() => {
    if (orderDirect) {
      return getCartTotal({
        orderDirect: true,
        cart: directRes?.data?.data.services || [],
        extras: directRes?.data?.data.extra_services || [],
        gift_values: directRes?.data?.data.gift_values || []
      });
    } else {
      return cartTotal;
    }
  }, [cartCombined, orderDirect]);

  useEffect(() => {
    setCartTotal(total);
  }, [total]);

  const onlyGift = only_gift({ cart, extras });
  useEffect(() => {
    // If it is not in the first page
    if (pageCount > 0) {
      const previousPageNumber = pageCount - 1;

      // If the previous page is not complete, go back to it
      const proceed = isComplete(
        (pageCount - 1) as proceed_val_type,
        orderDetails,
        deactivateBars,
        onlyGift
      );
      if (!proceed) {
        setPageCount(previousPageNumber);
      }
    }
  }, [pageCount]);

  const hasSettings =
    settings.order_creation_before_interval !== undefined &&
    settings.order_reschedule_interval !== undefined &&
    settings.minimum_order !== undefined;

  const customer_email = orderDetails.client_email;

  return hasSettings ? (
    <CheckOutProvider
      directDetails={directRes.data}
      direct={orderDirect || directUrl}
      noCouponCheck={orderSuccess}
    >
      <Suspense fallback={<></>}>
        <div className="checkOut_wrapper">
          <BlackListWrapper
            listObjects={[
              {
                inView: timeError,
                text: t(
                  "Please select a different date for your cleaning. The selected date is no longer available"
                ),
                watch: timeError,
                nonClosable: true
              }
            ]}
          />
          <CheckOutHeader deactivate={deactivateBars} />

          {deactivateBars ? (
            orderDirect ? (
              directRes.loading ? (
                t("Loading...")
              ) : directRes.data ? (
                <PaymentDirect directPaymentInfo={directRes.data} />
              ) : (
                <></>
              )
            ) : orderSuccess ? (
              <CheckOutSuccess customer_email={customer_email || ""} />
            ) : (
              <div style={{ textAlign: "center" }}>
                {directRes.loading ? t("Loading...") : ""}
              </div>
            )
          ) : (
            <>
              <CheckOutItemWrap page={0} pageCount={pageCount}>
                <CheckOutExtra settingRes={settingRes} />
              </CheckOutItemWrap>
              <CheckOutTime hide={pageCount !== 1} />
              <CheckOutItemWrap page={2} pageCount={pageCount}>
                <CheckOutAddress />
              </CheckOutItemWrap>
              <CheckOutItemWrap page={3} pageCount={pageCount}>
                <Payment />
              </CheckOutItemWrap>
            </>
          )}
        </div>
      </Suspense>
    </CheckOutProvider>
  ) : (
    <></>
  );
};

const mapStateToProps = (state: stateType) => ({
  pageCount: state.commonReducer.pageCount,
  cart: state.cartReducer.cart,
  extras: state.cartReducer.extras,
  orderDetails: state.orderReducer.details,
  orderDirect: state.orderReducer.direct,
  orderInfo: state.orderReducer.orderInfo,
  orderSuccess: state.orderReducer.success,
  settings: state.settingsReducer,
  timeError: state.commonReloadReducer.timeError
});

const mapDispatchToProps = (dispatch: any) => ({
  setPageCount: (data: number) => {
    dispatch(setPageCount(data));
  },
  setOrderDetails: (details: orderType) => {
    dispatch(setOrderDetails(details));
  },
  setDirectPayment: (data: boolean) => {
    dispatch(setDirectPayment(data));
  },
  setOrderInfo: (data: orderInfo) => {
    dispatch(setOrderInfo(data));
  },
  setPaymentSuccess: (data: boolean) => {
    dispatch(setPaymentSuccess(data));
  },
  setCheckOutError: (data: boolean) => {
    dispatch(setCheckOutError(data));
  },
  setTimeError: (data: boolean) => {
    dispatch(setTimeError(data));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckOut);

const CheckOutItemWrap = ({
  page,
  pageCount,
  children
}: {
  page: number;
  pageCount: proceed_val_type;
  children: ReactElement;
}) => {
  return page === pageCount ? <>{children}</> : <div></div>;
};

export const CheckOutSuccess = ({
  customer_email
}: {
  customer_email: string;
}) => {
  const { t, locale } = useContext(LocaleContext);

  return (
    <div className=" checkout_left check_out_successful_wrapper center fdCol">
      <div className="top center">
        <span className="suc_logo">
          <Tick className="img_div_contain" />
        </span>
        <span>
          {t("Thank you for your booking! Details have been emailed to you.")}
        </span>
      </div>
    </div>
  );
};

export const BlackListWrapper = ({
  listObjects
}: {
  listObjects: {
    text: string;
    inView: boolean;
    watch: boolean;
    nonClosable?: boolean;
  }[];
}) => {
  const show = listObjects?.some((item) => item.inView && item.text);
  return show ? (
    <div className="blacklist_wrapper">
      {listObjects.map(({ text, inView, watch, nonClosable }) => (
        <BlackList
          text={text}
          inView={inView}
          watch={watch}
          nonClosable={nonClosable}
        />
      ))}
    </div>
  ) : (
    <></>
  );
};

const BlackList = ({
  text,
  inView,
  watch,
  nonClosable
}: {
  text: string; // Text to display as notification
  inView: boolean; // Indicates whether or not to show indicator
  watch: boolean; // Value to be put in dependency array of useEffect
  nonClosable?: boolean; // whether or not a close button is shown. This also prevent spontaneous hiding on watch change
}) => {
  const [show, setShow] = useState(true);
  const [initialMount, setInitialMount] = useState(true);

  useEffect(() => {
    if (inView && !nonClosable) {
      setShow(watch);

      if (initialMount) {
        setInitialMount(false);
      }
    }
  }, [watch]);

  return inView && show ? (
    <div className="blacklist_error_wrapper">
      <div className="blacklist_error_container">{text}</div>
      <div className="blacklist_error_btn_close center">
        {!nonClosable && (
          <CloseIcon
            className="header_menu_close_icon img_div_contain"
            onClick={() => setShow(false)}
          />
        )}
      </div>
    </div>
  ) : (
    <></>
  );
};

/**
 * getDisabledDates: /api/get-disabled-days (POST)
 * checkAddress: /api/address/check (POST)
 * get_cleanerTime: /api/cleaners (POST)
 * getOrderPrice: /api/orders/calculate-amount (POST)
 * getDepositAmount: /api/orders/calculate-deposit (POST)
 */
